import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Star, Quote } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/dibvmg73_kens%20gold.png";

const timeline = [
  { phase: "The Beginning", title: "A Kitchen Experiment", description: "It started in a small kitchen with a simple question: 'What if I could fit the most powerful natural remedies into a single shot?' Ken began experimenting with fresh ginger, raw honey, and lemon \u2014 ingredients he had relied on for years to stay healthy." },
  { phase: "The Discovery", title: "Finding the Formula", description: "After months of testing, adding unfiltered Apple Cider Vinegar and premium green tea to the blend was the missing piece. The flavors balanced. The potency was unmatched. The first true Ken's Gold formula was born \u2014 raw, concentrated, and deeply nourishing." },
  { phase: "The Mission", title: "Sharing the Goodness", description: "Friends and family who tried it saw real results. Better digestion. More energy. A stronger immune system. Ken knew this was too good to keep to himself. With a commitment to quality and a passion for natural wellness, Ken's Gold was ready for the world." },
  { phase: "Today", title: "Nature in Every Bottle", description: "Every bottle of Ken's Gold is still made with the same care and dedication as that very first batch. No shortcuts. No compromises. Just the best raw, organic ingredients, carefully combined and bottled just for you." },
];

const promises = [
  { icon: <Sparkles size={24} />, title: "100% Raw & Organic", desc: "We never use artificial flavors, preservatives, or additives. Just pure, raw ingredients sourced from trusted, organic farms." },
  { icon: <Heart size={24} />, title: "Made with Love", desc: "Every batch is crafted with genuine attention to quality. We believe in what we make, and we take pride in every single bottle." },
  { icon: <Star size={24} />, title: "Proven by Tradition", desc: "Our formula is rooted in centuries of traditional use and backed by modern nutritional science. These ingredients have stood the test of time." },
];

/* ─── Sub-components ─── */

function OriginHero() {
  return (
    <section className="origin-hero py-16 md:py-24 px-6 text-center relative overflow-hidden" data-testid="origin-hero">
      <div className="origin-hero-bg" />
      <div className="relative z-10">
        <img src={LOGO_URL} alt="Ken's Gold" className="w-48 h-48 md:w-56 md:h-56 mx-auto mb-8 object-contain" />
        <div className="badge-gold mx-auto mb-6 bg-white/10 border-white/20 text-white/90">
          <Sparkles size={14} />The Story Behind Ken's Gold
        </div>
        <h1 className="font-playfair text-4xl md:text-6xl font-bold text-white leading-tight mb-4" data-testid="origin-title">
          Born from Nature, <br /><span className="text-gold-light italic">Made with Purpose</span>
        </h1>
        <p className="text-white/70 max-w-xl mx-auto text-base leading-relaxed">
          Ken's Gold is more than a wellness shot. It's the result of a lifelong passion for natural healing — distilled into one powerful bottle.
        </p>
      </div>
    </section>
  );
}

function MicroStoryHighlight() {
  return (
    <section className="py-14 px-6 bg-honey-tint" data-testid="micro-story-highlight">
      <div className="max-w-2xl mx-auto text-center">
        <Quote size={32} className="text-gold/30 mx-auto mb-4" />
        <p className="font-playfair text-xl md:text-2xl text-brown-dark leading-relaxed italic">
          "Ken started crafting this blend in his own kitchen — searching for a clean, powerful daily ritual that actually worked. After hundreds of tweaks, this is the formula that stuck."
        </p>
      </div>
    </section>
  );
}

function FounderSection() {
  return (
    <section className="py-16 md:py-24 px-6" data-testid="founder-section">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Our Origin &middot; Founded 2012</p>
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-brown-dark leading-tight mb-8">The Man Behind<br />The Gold</h2>
        <div className="space-y-5 text-gray-600 leading-relaxed">
          <p>Ken has always been fascinated by the healing power of nature. Growing up, his household relied on ginger tea for illness, honey for sore throats, and lemon water for energy in the morning. These weren't just old traditions — they worked, and Ken never forgot it.</p>
          <p>As he grew older and deepened his understanding of nutrition and herbal medicine, Ken wanted to bring these timeless remedies into the modern, busy lifestyle. The wellness shot was the perfect answer — potent, fast, and easy to take every single day.</p>
          <p className="text-brown-dark font-medium italic">"I didn't set out to build a brand. I just wanted something real — something my family could trust. That's still what drives every batch."</p>
        </div>
      </div>
    </section>
  );
}

function TimelineItem({ item }) {
  return (
    <div className="flex gap-6" data-testid={`timeline-${item.phase.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="timeline-dot mt-6" />
      <div className="timeline-card flex-1">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-1">{item.phase}</p>
        <h3 className="font-playfair text-xl font-bold text-brown-dark mb-2">{item.title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

function TimelineSection() {
  return (
    <section className="py-16 px-6" data-testid="timeline-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-center text-brown-dark italic mb-12">The Journey</h2>
        <div className="relative pl-8">
          <div className="timeline-line" />
          <div className="space-y-8">
            {timeline.map((item) => (
              <TimelineItem key={item.phase} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PromisesSection() {
  return (
    <section className="py-16 md:py-24 px-6" data-testid="promises-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-center text-brown-dark mb-12">Our Promise to You</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {promises.map((p) => (
            <div key={p.title} className="promise-card" data-testid={`promise-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="icon-circle mx-auto mb-5">{p.icon}</div>
              <h3 className="font-playfair text-xl font-bold text-brown-dark mb-3">{p.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TasteCta() {
  return (
    <section className="cta-gradient py-16 md:py-24 px-6 text-center" data-testid="taste-cta">
      <div className="max-w-2xl mx-auto">
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-white italic mb-6">Taste the Story</h2>
        <p className="text-white/70 text-base md:text-lg mb-8 leading-relaxed">
          Ready to experience Ken's Gold for yourself? Every sip carries decades of passion, knowledge, and genuine love for natural wellness.
        </p>
        <Link to="/shop" className="btn-gold text-lg" data-testid="origin-cta-btn">Take Your First Shot <ArrowRight size={18} /></Link>
        <p className="text-white/50 text-sm mt-4 italic">Your body will thank you.</p>
      </div>
    </section>
  );
}

/* ─── Main Component ─── */

export default function Origin() {
  useSEO({
    title: "Our Story",
    description: "The story behind Ken's Gold. Born from a passion for natural healing, Ken crafted the perfect wellness shot from raw, organic ingredients. Founded 2012. 100% raw & organic.",
  });

  return (
    <div data-testid="origin-page">
      <OriginHero />
      <MicroStoryHighlight />
      <FounderSection />
      <TimelineSection />
      <PromisesSection />
      <TasteCta />
    </div>
  );
}
